import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  DataGrid,
  type GridColDef,
  type GridRowId,
  type GridRowSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { Box } from '@mui/material';

import { useApi } from '@/providers/ApiContext';
import { listOrganizations } from '@/actions/org';
import type { Organization } from '@/types';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';

interface OrganizationsGridProps {
  selected_ids: string[];
  onSelectionChange: (selected_ids: string[]) => void;
  refresh_key: number;
}

function OrganizationsGridToolbar(): React.JSX.Element {
  return (
    <GridToolbarContainer
      sx={{
        '& .MuiButtonBase-root': {
          color: 'black',
        },
      }}
    >
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter debounceMs={500} placeholder='Search orgs...' />
    </GridToolbarContainer>
  );
}

function OrganizationsGrid({
  selected_ids,
  onSelectionChange,
  refresh_key,
}: OrganizationsGridProps): React.JSX.Element {
  const navigate = useNavigate();
  const api = useApi();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [api_error, setApiError] = useState<string | null>(null);

  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({
      type: 'include',
      ids: new Set<GridRowId>(selected_ids),
    }),
    [selected_ids],
  );

  const fetchOrganizations = useCallback(async (): Promise<void> => {
    if (!api) return;
    try {
      setLoading(true);
      const data = await listOrganizations(api);
      setOrganizations(data);
      setApiError(null);
    } catch (err) {
      const error = err as Error;
      setApiError(
        error.message ||
          'Unable to fetch organizations. This could be due to network issues or server problems.',
      );
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations, refresh_key]);

  const columns: GridColDef<Organization>[] = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
      {
        field: 'customer_domain',
        headerName: 'Customer Domain',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'tenant_domain',
        headerName: 'Tenant Domain',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'on_prem',
        headerName: 'On Prem',
        width: 110,
        type: 'boolean',
      },
      {
        field: 'api_endpoint',
        headerName: 'API Endpoint',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'te_endpoint',
        headerName: 'TE Endpoint',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'updated_at',
        headerName: 'Updated',
        width: 180,
      },
    ],
    [],
  );

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={organizations}
          columns={columns}
          getRowId={(row) => row.entity_id}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(model) =>
            onSelectionChange(Array.from(model.ids).map((id) => String(id)))
          }
          onRowDoubleClick={(params) => {
            navigate(`/organizations/edit?organization_id=${params.row.entity_id}`);
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{
            toolbar: OrganizationsGridToolbar,
          }}
        />
      </Box>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default OrganizationsGrid;

